import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import contentConfig from '../content-config.json';

const isDev = import.meta.env.DEV;

// In production, config is bundled at build time
const defaultConfig = contentConfig;

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const [config, setConfig] = useState(isDev ? {} : defaultConfig);
  const [configLoaded, setConfigLoaded] = useState(!isDev);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | 'error' | null
  const [editModeOn, setEditModeOn] = useState(false);

  const getText = useCallback(
    (path) => {
      const keys = path.split('.');
      let value = config;
      for (const key of keys) {
        value = value?.[key];
      }
      return typeof value === 'string' ? value : undefined;
    },
    [config]
  );

  const setText = useCallback((path, value) => {
    if (!isDev) return;
    setConfig((prev) => {
      const keys = path.split('.');
      const next = JSON.parse(JSON.stringify(prev));
      let current = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!(key in current) || typeof current[key] !== 'object') {
          current[key] = {};
        }
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;
      return next;
    });
  }, []);

  const save = useCallback(async () => {
    if (!isDev) return;
    setSaveStatus('saving');
    try {
      const res = await fetch('/api/save-content-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (e) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  }, [config]);

  // In dev: E toggles edit mode, Ctrl+S / Cmd+S saves
  useEffect(() => {
    if (!isDev) return;
    const handleKeyDown = (e) => {
      if (e.key === 'e' || e.key === 'E') {
        const inInput = document.activeElement?.closest('input, textarea, [contenteditable="true"]');
        if (!e.ctrlKey && !e.metaKey && !e.altKey && !inInput) {
          setEditModeOn((prev) => !prev);
        }
        return;
      }
      if ((e.key === 's' || e.key === 'S') && (e.ctrlKey || e.metaKey)) {
        if (editModeOn) {
          e.preventDefault();
          save();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDev, editModeOn, save]);

  // In dev, fetch config from API on mount (so we get latest from file)
  useEffect(() => {
    if (!isDev) return;
    fetch('/api/content-config')
      .then((res) => res.json())
      .then((data) => {
        setConfig(data);
        setConfigLoaded(true);
      })
      .catch(() => {
        setConfig(defaultConfig);
        setConfigLoaded(true);
      });
  }, []);

  const value = {
    config,
    configLoaded,
    getText,
    setText,
    save,
    saveStatus,
    editModeOn,
    setEditModeOn,
    isEditMode: isDev && editModeOn,
  };

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within ContentProvider');
  return ctx;
}
